
import {
  ComputedRef,
  defineComponent,
  onBeforeMount,
  PropType,
  ref,
  Ref
} from "vue";
import { useStore } from "vuex";
import { computed } from "@vue/reactivity";
import { dispatchErrorBox, dispatchSuccessBox } from "../../mixins";
import { useRoute } from "vue-router";
import router from "../../../../../src/routes";
import { Product } from "../../../../../domain/entities/shop/product/product";
import { User } from "../../../../../domain/entities/entities";
import { RepositoryException } from "../../../../../domain/exceptions/exceptions";
import HoverGallery from "./HoverGallery.vue";

export default defineComponent({
  name: "card",
  components: {
    HoverGallery
  },
  props: {
    product: {
      type: Object as PropType<Product>,
      required: true
    },
    user: {
      type: Object as PropType<User>,
      required: true
    }
  },

  setup(props) {
    const store = useStore();
    const route = useRoute();
    const autoPlayTimer = ref(0);

    /** Computed which contains the current page of the favorite list. */
    const currentPage: Ref<number> = ref(Number(route.query.page));

    /** Computed that contains the products added to the bag. */
    const bagProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInBag"];
    });

    /** Computed that contains the products added to favorites.*/
    const favoriteProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInFavoriteList"];
    });

    /** Getter of the decoins with the subtraction of the values of the products added in the bag. */
    const balance: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/balance"];
    });

    /** Function responsible for adding the product to the bag.
     * @param product parameter that receives an object of type Product as a variable. */
    async function addToBag(product: Product): Promise<void> {
      try {
        product.quantity = 1;

        if (product.stock <= 0) {
          return;
        } else if (product.decoins > props.user.decoins) {
          return;
        } else if (balance.value < product.decoins) {
          return;
        } else {
          await store.dispatch("shop_module/addToBag", product);
          await dispatchSuccessBox("Produto adicionado no seu carrinho!");
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for removing the product from the bag.
     * @param product parameter that receives an object of type Product as a variable. */
    async function removeFromBag(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/removeFromBag", product);
        await dispatchSuccessBox("Produto removido do seu carrinho!");
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for adding the product to favorites.
     * @param product parameter that receives an object of type Product as a variable. */
    async function addToFavorites(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/addToFavorite", product.id);
        await store.dispatch("shop_module/loadFavoriteProducts", {
          page: currentPage.value,
          limit: 6
        });
        isFavorite(product);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for removing the product from favorites.
     * @param product parameter that receives an object of type Product as a variable. */
    async function removeFromFavoriteList(product: Product): Promise<void> {
      try {
        await store.dispatch(
          "shop_module/removeProductFromFavorites",
          product.id
        );
        await store.dispatch("shop_module/loadFavoriteProducts", {
          page: currentPage.value,
          limit: 6
        });
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for checking if the product is in the bag.
     * @param product parameter that receives an object of type Product as a variable. */
    function bagIncludes(product: Product) {
      return bagProducts.value.find(item => item.id === product.id);
    }

    /** Function responsible for checking if the product is in the favorites.
     * @param product parameter that receives an object of type Product as a variable. */
    function isFavorite(product: Product) {
      return favoriteProducts.value.find(item => item.id === product.id);
    }

    /** Function responsible for setting the product id value in the URL.
     * @param product parameter that receives an object of type Product as a variable. */
    function gotToProductDetail(product: Product): void {
      router.push({
        name: "product_detail",
        params: { productID: product.id }
      });
    }

    /** Function responsible for validating the user's decoins. If the user does not have enough decoins to buy
     * the product, the add button in the bag is blocked.
     * @param product parameter that receives an object of type Product as a variable. */
    function hasEnoughDecoins(product: Product): boolean {
      return product.decoins > props.user.decoins;
    }

    /** Function responsible for checking if the product is still in stock.
     * @param product parameter that receives an object of type Product as a variable. */
    const isProductOutOfStock = (product: Product): boolean => {
      return product.stock < 1;
    };

    /** Function used to control whether for each product added to the bag it is still possible for the user to add
     * the other products that are available
     * @param product product to do validation. */
    function checkEnough(product: Product) {
      const remain = balance.value - product.decoins;
      const inBag = bagIncludes(product);

      if (product.stock <= 0) {
        return true;
      }

      if (inBag) {
        return true;
      }

      if (remain >= 0) {
        return true;
      }

      return false;
    }

    /** Function responsible for displaying a label informing the product status. */
    function setButtonText(product: Product): string {
      if (!checkEnough(product)) {
        return "decoins insuficientes";
      } else if (isProductOutOfStock(product)) {
        return "sem estoque";
      } else {
        return "adicionar";
      }
    }

    /** Function responsible for formatting the decoins to Brazilian currency. */
    function formatDecoins(decoins: number) {
      return Intl.NumberFormat("pt-br").format(decoins);
    }

    return {
      bagProducts,
      favoriteProducts,
      balance,
      autoPlayTimer,
      addToBag,
      removeFromBag,
      bagIncludes,
      addToFavorites,
      isFavorite,
      removeFromFavoriteList,
      gotToProductDetail,
      hasEnoughDecoins,
      isProductOutOfStock,
      checkEnough,
      setButtonText,
      formatDecoins
    };
  }
});
